"use client";

import Image from "next/image";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { HOME_COPY } from "@/lib/landing-copy";
import { HeadlineLines } from "./headline-lines";
import { DATA_LABEL_CLS, DISPLAY, EDITORIAL_ACTION_CLS, LABEL_CLS } from "./theme";
import { LogoGrid } from "./primitives";
import { MERCURY_CLIENTS } from "./clients";
import { EvidenceSourcesGrid } from "./evidence-sources-grid";
import { EvidenceEgress, type EgressLanding } from "./glass-twin/evidence-egress";
import { runOnceTwinClock, TWIN_SETTLED_SECONDS } from "./glass-twin/sequence";
import { TwinClockProvider } from "./glass-twin/sequence-context";
import { IngressLayer, LiveInstrument } from "@/components/editorial/bands/hero-instrument";
import { CtaPair } from "@/components/editorial/bands/pages";
import styles from "./landing-hero.module.css";

const INK_MUTED = "text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

export type LandingHeroContent = {
  label: string;
  title: readonly [string, string, string];
  lead: readonly (string | { name: string; logo: string })[];
  cta: string;
  buyer: string;
  instrumentLabel: string;
  /** A sector's arrangement of the connector grid; without one, the full grid. */
  integrations?: "accountants" | "consultancies";
};

/** The clients Mercury Labs has delivered for. The Crown Commercial Service
 *  mark is a framework status, not a client, so the home's trust band
 *  carries it instead (the 2026-09-30 copy review). */
const DELIVERED_FOR = MERCURY_CLIENTS.filter((client) => client.name !== "Crown Commercial Service");

/** The client roster, light variant with hairlines, as landing furniture. */
export function LogoStrip() {
  return (
    <section className="border-b border-dotted border-[var(--rule)] py-10">
      <p className={`${INK_MUTED} ${DATA_LABEL_CLS}`}>Mercury Labs has delivered for</p>
      <div className="mt-5">
        <LogoGrid variant="light" clients={DELIVERED_FOR} />
      </div>
      <p className={`mt-4 ${INK_MUTED} text-[0.72rem] leading-[1.5]`}>
        Client work by Mercury Labs, the team behind your Twin.
      </p>
    </section>
  );
}

/*
 * The resolving hero (the home's, since 2026-10-01): the instrument plays
 * its sequence once, faster than it is written, and the product takes the
 * stage when the rings have settled. The records land in about two and a
 * half seconds, the rings settle at five and the product is in view by six.
 */

/** How many times its written pace the sequence plays. */
const RESOLVE_RATE = 4.2;
/** The settled rings, held before the product takes the stage. */
const RESOLVE_HOLD_MS = 450;
/** If the twin cannot draw this long after the page loads, the product takes the stage without it. */
const RESOLVE_WAIT_MS = 3500;
/** The swap's length (landing-hero.module.css); the instrument leaves the page after it. */
const RESOLVE_SWAP_MS = 1100;

/** The records the answer cites, as the product links them. */
function citedRecords(product: HTMLElement): Element[] {
  return [...product.querySelectorAll('a[data-workspace-link="record"]')];
}

/** The systems on the answer's "Retrieved with" line: the marks beside that label. */
function retrievedWith(product: HTMLElement): Element[] {
  const label = [...product.querySelectorAll("span")].find((span) => span.textContent?.trim().toLowerCase() === "retrieved with");
  return [...(label?.nextElementSibling?.children ?? [])];
}

/**
 * Where the rings' particles land as the product arrives (Archie,
 * 2026-10-01): on each record the answer cites, one after another, then on
 * the systems it was retrieved with.
 */
const HERO_LANDINGS: readonly EgressLanding[] = [
  { find: citedRecords, count: 48, leadMs: 0, stepMs: 70, landedClass: styles.landedRecord ?? "" },
  { find: retrievedWith, count: 10, leadMs: 220, stepMs: 35, landedClass: styles.landedSource ?? "" },
];

/** Waiting for the twin, playing the sequence, swapping to the product, then the product alone. */
type Stage = "waiting" | "running" | "resolved" | "settled";

function useResolvingStage(enabled: boolean) {
  const [startMs, setStartMs] = useState<number | null>(null);
  const [stage, setStage] = useState<Stage>("waiting");
  /** Whether the swap began from the settled rings: only then do particles leave them for the product. */
  const [fromRings, setFromRings] = useState(false);

  const start = useCallback(() => {
    setStartMs((current) => current ?? performance.now());
    setStage((current) => (current === "waiting" ? "running" : current));
  }, []);

  useEffect(() => {
    if (enabled && window.matchMedia("(prefers-reduced-motion: reduce)").matches) setStage("settled");
  }, [enabled]);

  useEffect(() => {
    if (!enabled || stage === "settled") return;
    const [next, after]: [Stage, number] =
      stage === "waiting"
        ? ["resolved", RESOLVE_WAIT_MS]
        : stage === "running"
          ? ["resolved", (TWIN_SETTLED_SECONDS / RESOLVE_RATE) * 1000 + RESOLVE_HOLD_MS]
          : ["settled", RESOLVE_SWAP_MS];
    const timer = window.setTimeout(() => {
      if (stage === "running") setFromRings(true);
      setStage(next);
    }, after);
    return () => window.clearTimeout(timer);
  }, [enabled, stage]);

  const clock = useMemo(() => runOnceTwinClock(RESOLVE_RATE, startMs), [startMs]);
  return { stage, clock, start, fromRings };
}

export function WorkingPageHero({
  production = false,
  content,
  product,
  actions,
}: {
  production?: boolean;
  content?: LandingHeroContent;
  /** The product the instrument resolves into. Without one the instrument loops, as the sector pages run it. */
  product?: ReactNode;
  /** The actions under the lead, in place of the site's pair. */
  actions?: ReactNode;
} = {}) {
  const resolving = product !== undefined;
  const { stage, clock, start, fromRings } = useResolvingStage(resolving);
  const instrumentOn = !resolving || stage !== "settled";
  const instrument = (
    <LiveInstrument
      sizeClass="w-[min(70vw,29rem)]"
      label={content?.instrumentLabel ?? "Your Twin · From records to useful work"}
      onReady={resolving ? start : undefined}
    />
  );
  const header = (
    <header
      id={content ? "hero" : undefined}
      data-twin-stage
      className="relative isolate border-b border-dotted border-[var(--rule)]"
    >
      {instrumentOn ? <IngressLayer /> : null}
      <div className={`relative z-[2] pb-14 sm:pb-16 ${production ? "pt-32 sm:pt-36" : "pt-14 sm:pt-16"}`}>
        {/* The resolving hero splits evenly: the product has a little more
            room before it runs off the page's right edge. */}
        <div
          className={`grid gap-10 lg:items-center ${
            resolving ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]"
          }`}
        >
          <div>
            {content && <p className={`${LABEL_CLS} mb-5 text-[var(--theme-accent-text)]`}>Your Twin / {content.label}</p>}
            <h1
              style={DISPLAY}
              className={`${INK_88} max-w-[12ch] text-[clamp(2.9rem,6.2vw,5rem)] leading-[0.98] font-semibold tracking-[-0.04em]`}
            >
              <HeadlineLines lines={content?.title ?? HOME_COPY.headline} />
            </h1>
            <p className={`mt-5 max-w-[50ch] ${INK_72} text-[1.05rem] leading-[1.65]`}>
              {content ? content.lead.map(part => typeof part === "string" ? part : (
                <span key={part.name} className="inline-flex items-baseline gap-[0.3em] whitespace-nowrap">
                  <Image src={part.logo} alt="" width={18} height={18} className="relative top-[0.12em] size-[1em] shrink-0 object-contain" />
                  {part.name}
                </span>
              )) : HOME_COPY.lead}
            </p>
            {/* One action and one way to read on: the step list and the
                changelog link left the hero in the 2026-09-30 copy review. */}
            <div className="mt-7">
              {actions ?? (content ? <div className="flex flex-wrap items-center gap-3">
                <a href="#waitlist" data-cta-placement="hero" className={`${EDITORIAL_ACTION_CLS} bg-[var(--theme-dark)] text-[var(--theme-ground)]`}>{content.cta}</a>
                <a href="#workflow" className={`${EDITORIAL_ACTION_CLS} border border-[var(--rule)] ${INK_88}`}>Why a Twin?</a>
              </div> : <CtaPair />)}
            </div>
            {content && <p className={`mt-5 text-[0.72rem] leading-relaxed ${INK_MUTED}`}>{content.buyer}</p>}
          </div>
          {resolving ? (
            <div className={styles.stage} data-stage={stage}>
              {instrumentOn ? <div className={styles.instrument}>{instrument}</div> : null}
              <div className={styles.product} data-egress-product="">
                {product}
              </div>
              {fromRings ? <EvidenceEgress className={styles.egress} landings={HERO_LANDINGS} /> : null}
            </div>
          ) : (
            instrument
          )}
        </div>
        {/* The home hero's connector grid, riser and all: the systems below
            feed the instrument above, on the same sequence clock. The lg
            margin leaves the riser's 140px clear of the row above, and of
            the product's shadow where the hero resolves into one. */}
        <div className={`mt-10 ${resolving ? "lg:mt-48" : "lg:mt-36"}`}>
          <EvidenceSourcesGrid showRiser variant={content?.integrations} />
        </div>
      </div>
    </header>
  );
  return resolving ? <TwinClockProvider value={clock}>{header}</TwinClockProvider> : header;
}
